import { IWorksheetColumn, IStringValue } from '../../types/types';
import { DataType, Strings } from '../../types/enums';

// TransactionType is the Enum column where real, persisted ValidationInfo content is used
// in place of any constructed/cmx-lookup text; it only falls back to the generic constructed
// hint text below when no real value exists yet. All other Enum columns always display the
// generic constructed text, prefixed by the Required text (if applicable) and preceded by any
// real, persisted ValidationInfo text (if present).
const TRANSACTION_TYPE_CLASS_NAME = 'TransactionType';

const isTransactionType = (column: Pick<IWorksheetColumn, 'className'>): boolean =>
    (column.className ?? '').toLowerCase() === TRANSACTION_TYPE_CLASS_NAME.toLowerCase();

// TransactionDate (the Trade Date column) reuses SecurityType's fully constructed display text
// verbatim - this mirrors the generator service's rule that SecurityType lookup validation text
// also drives TransactionDate. Keep this coupling intact when changing validation behavior.
const SECURITY_TYPE_CLASS_NAME = 'SecurityType';
const TRANSACTION_DATE_CLASS_NAME = 'TransactionDate';

const isSecurityType = (column: Pick<IWorksheetColumn, 'className'>): boolean =>
    (column.className ?? '').toLowerCase() === SECURITY_TYPE_CLASS_NAME.toLowerCase();

const isTransactionDate = (column: Pick<IWorksheetColumn, 'className'>): boolean =>
    (column.className ?? '').toLowerCase() === TRANSACTION_DATE_CLASS_NAME.toLowerCase();

export interface IValidationInfoDisplayStrings {
    requiredPrefixText: string;
    enumListSuffixText: string;
    cmxLookupTableTextFormat: string;
}

export const VALIDATION_INFO_DISPLAY_STRING_KEYS: string[] = [
    Strings.ValidationInfoRequiredPrefix,
    Strings.ValidationInfoEnumListSuffix,
    Strings.SubmissionTemplateCmxLookupTableText,
];

export const getValidationInfoDisplayStrings = (strings: IStringValue[] | null | undefined): IValidationInfoDisplayStrings => {
    const getValue = (key: string) => strings?.find(s => s.key?.toLowerCase() === key.toLowerCase())?.value ?? '';

    return {
        requiredPrefixText: getValue(Strings.ValidationInfoRequiredPrefix),
        enumListSuffixText: getValue(Strings.ValidationInfoEnumListSuffix),
        cmxLookupTableTextFormat: getValue(Strings.SubmissionTemplateCmxLookupTableText),
    };
};

// Computes the read-only Instructions/ValidationInfo text to display for a worksheet column,
// without ever mutating the column's real, persisted validationInfo value. This mirrors the
// server's previous ApplyEnumValidationInfoForDisplayAsync construction, moved entirely to the
// client so the true DB value round-trips through save untouched.
//
// Every column type displays: Required prefix (if applicable) + real ValidationInfo (if present).
// Enum columns additionally append the generic constructed cmx-lookup-table text - except
// TransactionType, which omits that constructed text whenever a real value is present.
//
// Legacy data note: the removed server-side ApplyEnumValidationInfoForDisplayAsync used to persist
// its constructed prefix/enum text directly onto WorksheetColumn.ValidationInfo on every save, so
// existing Enum rows can already contain that generated text as their "real" value. If we treated
// that legacy payload as genuine user-entered content and then appended the freshly-constructed
// enum text again, it would visibly duplicate the suffix (and prefix). isLegacyGeneratedValue below
// detects a persisted value that exactly matches what the old logic would have produced for this
// column, so it can be excluded rather than shown twice.
const buildLegacyGeneratedValue = (
    column: Pick<IWorksheetColumn, 'fieldIsRequired'>,
    enumText: string,
    displayStrings: IValidationInfoDisplayStrings
): string =>
    [
        column.fieldIsRequired ? displayStrings.requiredPrefixText : '',
        enumText,
    ].filter(line => !!line && line.trim().length > 0).join('\n');

const isLegacyGeneratedValue = (
    column: Pick<IWorksheetColumn, 'fieldIsRequired'>,
    realValue: string,
    enumText: string,
    displayStrings: IValidationInfoDisplayStrings
): boolean => {
    const normalizedReal = realValue.trim();

    if (!normalizedReal) {
        return false;
    }

    // Compare against both the required and non-required constructed forms, since the legacy
    // value may have been persisted while the column had a different Required setting than now.
    const withPrefix = buildLegacyGeneratedValue({ fieldIsRequired: true }, enumText, displayStrings).trim();
    const withoutPrefix = buildLegacyGeneratedValue({ fieldIsRequired: false }, enumText, displayStrings).trim();

    return (!!withPrefix && normalizedReal === withPrefix) || (!!withoutPrefix && normalizedReal === withoutPrefix);
};

export const getValidationInfoDisplayText = (
    column: Pick<IWorksheetColumn, 'dataTypeId' | 'fieldIsRequired' | 'validationInfo' | 'className'>,
    displayStrings: IValidationInfoDisplayStrings,
    siblingColumns?: Pick<IWorksheetColumn, 'dataTypeId' | 'fieldIsRequired' | 'validationInfo' | 'className'>[]
): string => {
    if (isTransactionDate(column)) {
        const securityTypeColumn = siblingColumns?.find(sibling => isSecurityType(sibling));
        if (securityTypeColumn) {
            return getValidationInfoDisplayText(securityTypeColumn, displayStrings);
        }
    }

    const isEnum = column.dataTypeId === DataType.Enum;
    const enumText = displayStrings.cmxLookupTableTextFormat
        ? displayStrings.cmxLookupTableTextFormat.replace('{0}', `\n${displayStrings.enumListSuffixText}`)
        : '';

    const realValue = column.validationInfo ?? '';
    const isLegacyValue = isEnum && isLegacyGeneratedValue(column, realValue, enumText, displayStrings);
    const hasRealValue = !isLegacyValue && !!realValue && realValue.trim().length > 0;

    const includeEnumText = isEnum && !(isTransactionType(column) && hasRealValue);

    return [
        column.fieldIsRequired ? displayStrings.requiredPrefixText : '',
        hasRealValue ? realValue : '',
        includeEnumText ? enumText : '',
    ].filter(line => !!line && line.trim().length > 0).join('\n');
};
